import Page from '../../../../enums/Page';
import VerificationProvider from '../../../../enums/VerificationProvider';
import VerificationType from '../../../../enums/VerificationType';
import {showOverlay} from '../../../actions/overlay-actions';
import setDeviceDataCollectionComplete from '../../../actions/paymentActions/setDeviceDataCollectionComplete';
import {
  initiateDeviceDataCollection,
  setErrorOnPage,
  updateShouldShowThreeDsChallengeIframe,
  updateThreeDSecureDataForChallengeIframe,
} from '../../../actions/paymentActions/threedsActions';
import {TooltipAndOverlayIDs} from '../../../components/detailed-tooltip/TooltipLibrary';
import getPage from '../../../page/getPage';
import {getSettings, isOrderProcessing} from '../../../selectors/selectors';
import getThreeDSecureData from '../../../selectors/ThreeDSSelectors/getThreeDSecureData';
import store from '../../../store/store';
import doMetric from '../../../tracking/bigQueryMetricRecorder';
import Track from '../../../utils/tracking';
import * as pollers from '../../pollers/pollers';

const handleError = (isOnOrderProcessingPage) => {
  if (!isOnOrderProcessingPage) {
    store.dispatch(setErrorOnPage());
  } else {
    store.dispatch(setErrorOnPage());
    return Promise.reject();
  }
};

export function handleThreeDSecureRequired(orderJSON) {
  const {threeDS} = orderJSON;
  const {verificationProvider, verificationType} = threeDS;
  
  const isOnOrderProcessingPage = getPage() === Page.PROCESSING;
  const isOrderInProcessingState = isOrderProcessing(store.getState());
  const isAdditionalLoggingEnabled = getSettings(store.getState())?.enable_additional_3ds_logging;
  
  const threeDSecureData = {
    issuerURL: threeDS.redirectUrl,
    oneTime3DSToken: threeDS.token,
    verificationProvider,
    verificationType,
    bin: threeDS.bin,
    merchantData: threeDS.merchantData,
    providerRedirectionInterceptor: threeDS.providerRedirectionInterceptor,
    providerTransactionId: threeDS.providerTransactionId,
    sessionId: threeDS.sessionId,
  };
  
  const threeDSecureDataFromState = getThreeDSecureData(store.getState());
  
  if (threeDSecureDataFromState.verificationProvider === threeDSecureData.verificationProvider
    && threeDSecureDataFromState.verificationType === threeDSecureData.verificationType
    && threeDSecureDataFromState.verificationProvider === VerificationProvider.NUVEI) {
    if (isAdditionalLoggingEnabled) {
      try {
        doMetric(
          'handleThreeDSecureRequired',
          [
            {name: 'shouldBackgroundPoll', value: true},
            {name: 'verificationProvider', value: threeDSecureData.verificationProvider},
            {name: 'verificationType', value: threeDSecureData.verificationType},
            {name: 'isOrderProcessing', value: isOrderInProcessingState},
            {name: 'isOnOrderProcessingPage', value: isOnOrderProcessingPage},
            {name: 'codeLocation', value: '1'},
          ], [], 'impression',
        );
      } catch (error) {
        Track.track(`Failed to submit data to bigquery handleThreeDSecureRequired isBackgroundPolling true, ${error}`);
      }
    }
    
    return 'START_BACKGROUND_POLL';
  }
  
  pollers.placeOrderViaApiPoller.stopPoller();
  
  // REDIRECT  -> 3DS1 -> show bank page inside an overlay (same for both providers adyen/worldpay/checkoutcom/chase)
  // IDENTIFY  -> 3DS2 -> initiate device collection on background (inside a hidden iframe)
  // CHALLENGE -> 3DS2 -> initiate challenge process (different for each provider)
  
  if (verificationType === VerificationType.REDIRECT || verificationType === VerificationType.CHALLENGE) {
    if (verificationProvider === VerificationProvider.NUVEI) {
      store.dispatch(setDeviceDataCollectionComplete());
      
      if (isOnOrderProcessingPage) {
        store.dispatch(updateThreeDSecureDataForChallengeIframe({...threeDSecureData, shouldShowChallengeIframe: true}));
      } else {
        store.dispatch(updateThreeDSecureDataForChallengeIframe(threeDSecureData));
        store.dispatch(showOverlay(TooltipAndOverlayIDs.THREE_D_SECURE));
      }
  
      if (isAdditionalLoggingEnabled) {
        try {
          doMetric(
            'handleThreeDSecureRequired',
            [
              {name: 'shouldBackgroundPoll', value: true},
              {name: 'verificationProvider', value: threeDSecureData.verificationProvider},
              {name: 'verificationType', value: threeDSecureData.verificationType},
              {name: 'isOrderProcessing', value: isOrderInProcessingState},
              {name: 'isOnOrderProcessingPage', value: isOnOrderProcessingPage},
              {name: 'codeLocation', value: '2'},
            ], [], 'impression',
          );
        } catch (error) {
          Track.track(`Failed to submit data to bigquery handleThreeDSecureRequired isBackgroundPolling true, ${error}`);
        }
      }
      
      return 'START_BACKGROUND_POLL';
    }
      
    store.dispatch(updateThreeDSecureDataForChallengeIframe(threeDSecureData));
    if (isOnOrderProcessingPage) {
      store.dispatch(updateShouldShowThreeDsChallengeIframe(true));
    } else {
      store.dispatch(showOverlay(TooltipAndOverlayIDs.THREE_D_SECURE));
    }
  } else if (verificationType === VerificationType.IDENTIFY) {
    if (verificationProvider === VerificationProvider.WORLDPAY) {
      const {bin, oneTime3DSToken, issuerURL} = threeDSecureData;
      if (bin && oneTime3DSToken && issuerURL) {
        store.dispatch(initiateDeviceDataCollection(threeDSecureData));
      } else {
        return handleError(isOnOrderProcessingPage);
      }
    } else if (verificationProvider === VerificationProvider.CHECKOUT_COM
      || verificationProvider === VerificationProvider.CHASE) {
      const {issuerURL, oneTime3DSToken, providerTransactionId} = threeDSecureData;
      if (issuerURL && oneTime3DSToken && providerTransactionId) {
        store.dispatch(initiateDeviceDataCollection(threeDSecureData));
      } else {
        return handleError(isOnOrderProcessingPage);
      }
    } else if (verificationProvider === VerificationProvider.NUVEI) {
      const {oneTime3DSToken, issuerURL} = threeDSecureData;
      if (oneTime3DSToken && issuerURL) {
        store.dispatch(initiateDeviceDataCollection(threeDSecureData));
  
        if (isAdditionalLoggingEnabled) {
          try {
            doMetric(
              'handleThreeDSecureRequired',
              [
                {name: 'shouldBackgroundPoll', value: true},
                {name: 'verificationProvider', value: threeDSecureData.verificationProvider},
                {name: 'verificationType', value: threeDSecureData.verificationType},
                {name: 'isOrderProcessing', value: isOrderInProcessingState},
                {name: 'isOnOrderProcessingPage', value: isOnOrderProcessingPage},
                {name: 'codeLocation', value: '3'},
              ], [], 'impression',
            );
          } catch (error) {
            Track.track(`Failed to submit data to bigquery handleThreeDSecureRequired isBackgroundPolling true, ${error}`);
          }
        }
        
        return 'START_BACKGROUND_POLL';
      }
      return handleError(isOnOrderProcessingPage);
    } else if (verificationProvider === VerificationProvider.ADYEN) {
      store.dispatch(initiateDeviceDataCollection(threeDSecureData));
    }
  }
}
